/**
 * audio-envelope-stream.js — what an editor needs of a LONG recording, without the recording in
 * memory: the waveform, the level for «find the silences», and a spectrogram, from one streamed
 * pass over the file.
 *
 * WHY (measured, RU, 01.09–01.10.2026). /chapter-splitter decoded the whole audiobook into an
 * AudioBuffer: 12 h of MP3 is ~15 GB of float PCM at 48 kHz. 249 sessions brought a file over
 * 150 MB; 65 of them ended on the device-memory wall («~717 min against a ceiling of ~352 min»)
 * or a decoder timeout (a 278 MB m4a: «tier1.5 decode timed out after 600000ms»), and on a phone
 * the wall stands at ~13 min — a 14 MB MP3 was refused. The wall then offered to cut the book
 * «into parts» — on the page whose job is cutting the book. The editor never needed the samples:
 * it draws min/max, sums squares over 20 ms for the silence finder, and takes one FFT per pixel
 * column. All three are a few tens of MB for any length, so the decode can be streamed and
 * thrown away as it goes.
 *
 * LANES (the bytes decide, not the name):
 *   wav — PCM/float payload read in 4 MB slices from the disk, no decoder at all;
 *   mp3 — frames walked in slices (Mp3FrameTrim's header parser) and decoded by WebCodecs, the
 *         queue held under a ceiling — Mp3DecoderStream reads the whole file and keeps an index
 *         of every frame, which is the problem this module exists to avoid;
 *   mp4 — m4a/m4b/mp4/mov through WebCodecsAudioDecode.decodeRange in its streaming form (only
 *         the audio bytes are read, windows ≤ 8 MB, the decoder's queue bounded).
 *   Anything else, or a browser without what the lane needs → a rejection with `.expected`;
 *   the caller keeps its whole-buffer path for that file.
 *
 * WHAT COMES BACK — channel 0, the channel the editor always read:
 *   { lane, sampleRate, channels, frames, duration,
 *     win,                     frames per window = floor(sampleRate × 0.02), the silence finder's own
 *     length, lastN,           windows, and frames in the last (short) one
 *     min, max,                Float32Array per window — the waveform
 *     ms,                      Float32Array per window — mean square: rms = √ms, exactly the
 *                              20-ms RMS the silence finder computed over the whole buffer
 *     spec: { every, size, bins, count, data } }
 *                              one FFT of `size` samples centred every `every` frames, kept as
 *                              0..255 = clamp((20·log10|X| + 60) / 60) per bin — the value the
 *                              editor's colour map takes
 *
 *   await AudioEnvelopeStream.lane(file)                 → 'wav' | 'mp3' | 'mp4' | null
 *   await AudioEnvelopeStream.read(file, { durationHint, columns, size, onProgress, signal })
 *   AudioEnvelopeStream.accumulator(sampleRate, { estFrames, columns, size })   (the core; tested
 *     in node: tests/js/audio-envelope-stream.test.mjs)
 */
(function (root) {
    'use strict';

    var SLICE = 4 * 1048576;
    var MP3_GROUP = 32;        // frames per EncodedAudioChunk (see readMp3)
    var MP3_QUEUE_MAX = 8;     // chunks waiting in the decoder

    function expected(msg) { var e = new Error(msg); e.expected = true; return e; }
    function aborted(signal) { return !!(signal && signal.aborted); }
    function abortError() { var e = new Error('aborted'); e.name = 'AbortError'; return e; }

    /* ── FFT (radix-2, in place) — the same transform the editor runs per column ── */
    function fft(re, im, n) {
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { var tr = re[i]; re[i] = re[j]; re[j] = tr; var ti = im[i]; im[i] = im[j]; im[j] = ti; }
        }
        for (var size = 2; size <= n; size <<= 1) {
            var half = size >> 1, ang = -2 * Math.PI / size;
            for (var k = 0; k < half; k++) {
                var wr = Math.cos(ang * k), wi = Math.sin(ang * k);
                for (var a = k; a < n; a += size) {
                    var b = a + half;
                    var xr = re[b] * wr - im[b] * wi, xi = re[b] * wi + im[b] * wr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi;
                    re[a] += xr; im[a] += xi;
                }
            }
        }
    }

    /* ── the core: channel-0 samples in, windows and spectrum columns out ── */
    function accumulator(sampleRate, opts) {
        opts = opts || {};
        var win = Math.max(1, Math.floor(sampleRate * 0.02));
        var est = opts.estFrames > 0 ? opts.estFrames : sampleRate * 600;
        var cap = Math.ceil(est / win) + 2;
        var min = new Float32Array(cap), max = new Float32Array(cap), ms = new Float32Array(cap);
        var len = 0, n = 0, lo = Infinity, hi = -Infinity, sq = 0, frames = 0;

        var size = opts.size || 1024, bins = size >> 1;
        var columns = opts.columns > 0 ? opts.columns : 2048;
        var every = Math.max(1, Math.floor(est / columns));
        var mask = size - 1;   // size is a power of two (the FFT needs it anyway)
        var ring = new Float32Array(size), hann = new Float32Array(size);
        for (var h = 0; h < size; h++) hann[h] = 0.5 * (1 - Math.cos(2 * Math.PI * h / (size - 1)));
        var re = new Float32Array(size), im = new Float32Array(size);
        var specCap = Math.ceil(est / every) + 2, spec = new Uint8Array(specCap * bins), count = 0;
        var nextDue = size >> 1;   // column 0 is centred on frame 0: ready once size/2 frames are in

        function grow() {
            var c = min.length * 2;
            var a = new Float32Array(c); a.set(min); min = a;
            var b = new Float32Array(c); b.set(max); max = b;
            var m = new Float32Array(c); m.set(ms); ms = m;
        }
        function closeWindow() {
            if (len >= min.length) grow();
            min[len] = lo; max[len] = hi; ms[len] = sq / n; len++;
            n = 0; lo = Infinity; hi = -Infinity; sq = 0;
        }
        /* The column centred on frame c covers [c - size/2, c + size/2); samples outside the
           recording are zero — the editor's own rule. `end` is the frame just past the window;
           the ring holds the `size` frames before `frames`. */
        function column(end) {
            var start = end - size;
            for (var i = 0; i < size; i++) {
                var f = start + i, v = 0;
                if (f >= 0 && f < frames && f >= frames - size) v = ring[f & mask];
                re[i] = v * hann[i]; im[i] = 0;
            }
            fft(re, im, size);
            if ((count + 1) * bins > spec.length) { var s2 = new Uint8Array(spec.length * 2); s2.set(spec); spec = s2; }
            var o = count * bins;
            for (var b = 0; b < bins; b++) {
                var mag = Math.sqrt(re[b] * re[b] + im[b] * im[b]);
                var val = (20 * Math.log10(Math.max(mag, 1e-10)) + 60) / 60;
                spec[o + b] = val <= 0 ? 0 : val >= 1 ? 255 : Math.round(val * 255);
            }
            count++;
            /* An estimate far below the truth would keep adding columns: past 4× the asked
               number, keep every other one and halve the rate — centres stay k·every. */
            if (count >= columns * 4 && (count & 1) === 0) {
                for (var k = 0; k < count / 2; k++) spec.copyWithin(k * bins, 2 * k * bins, 2 * k * bins + bins);
                count /= 2; every *= 2;
                nextDue = count * every + (size >> 1) - every;   // the caller adds `every` next
            }
        }

        return {
            win: win,
            /* The hot loop keeps its state in locals and writes it back once: closure variables
               cost ~70 ns a sample here (58 s for 10 hours at 22 kHz), locals a fraction of it. */
            push: function (ch0, length) {
                var m = length == null ? ch0.length : length;
                var l = lo, h = hi, q = sq, k = n, f = frames, due = nextDue;
                for (var i = 0; i < m; i++) {
                    var v = ch0[i];
                    if (v < l) l = v;
                    if (v > h) h = v;
                    q += v * v;
                    ring[f & mask] = v;
                    f++;
                    if (++k === win) {
                        if (len >= min.length) grow();
                        min[len] = l; max[len] = h; ms[len] = q / k; len++;
                        k = 0; l = Infinity; h = -Infinity; q = 0;
                    }
                    if (f === due) { frames = f; column(f); nextDue += every; due = nextDue; }
                }
                lo = l; hi = h; sq = q; n = k; frames = f;
            },
            finish: function () {
                var lastN = n || win;
                if (n > 0) closeWindow();
                // columns centred inside the recording whose window runs past its end
                while (nextDue - (size >> 1) < frames) { column(nextDue); nextDue += every; }
                return {
                    sampleRate: sampleRate, frames: frames, duration: frames / sampleRate,
                    win: win, length: len, lastN: len ? lastN : 0,
                    min: min.subarray(0, len), max: max.subarray(0, len), ms: ms.subarray(0, len),
                    spec: { every: every, size: size, bins: bins, count: count, data: spec.subarray(0, count * bins) }
                };
            }
        };
    }

    /* ── WAV: the payload is the PCM, read it straight off the disk ── */
    var KNOWN_CHUNKS = ['data', 'fmt ', 'LIST', 'fact', 'bext', 'id3 ', 'ID3 ', 'JUNK', 'PAD ', 'cue ', 'smpl', 'inst', 'iXML', 'acid', 'umid', 'PEAK', 'cart'];
    function tag4(u8, o) { return (o < 0 || o + 4 > u8.length) ? '' : String.fromCharCode(u8[o], u8[o + 1], u8[o + 2], u8[o + 3]); }
    function isTag(u8, o) {
        if (o < 0 || o + 4 > u8.length) return false;
        for (var i = 0; i < 4; i++) if (u8[o + i] < 0x20 || u8[o + i] > 0x7E) return false;
        return true;
    }
    function wavLayout(u8) {
        if (u8.length < 12) return null;
        var dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
        var tag = function (o) { return String.fromCharCode(u8[o], u8[o + 1], u8[o + 2], u8[o + 3]); };
        var riff = tag(0);
        if ((riff !== 'RIFF' && riff !== 'RF64') || tag(8) !== 'WAVE') return null;
        var off = 12, fmt = null, data = null, ds64 = 0;
        while (off + 8 <= u8.length) {
            var id = tag(off), size = dv.getUint32(off + 4, true);
            if (id === 'ds64' && off + 24 <= u8.length) ds64 = dv.getUint32(off + 16, true) + dv.getUint32(off + 20, true) * 4294967296;
            if (id === 'fmt ' && off + 24 <= u8.length) {
                var tagFmt = dv.getUint16(off + 8, true);
                if (tagFmt === 0xFFFE && off + 34 <= u8.length) tagFmt = dv.getUint16(off + 32, true);
                fmt = { format: tagFmt, channels: dv.getUint16(off + 10, true), sampleRate: dv.getUint32(off + 12, true),
                        blockAlign: dv.getUint16(off + 20, true), bits: dv.getUint16(off + 22, true) };
            }
            if (id === 'data') { data = { offset: off + 8, bytes: (size === 0xFFFFFFFF && ds64) ? ds64 : size }; break; }
            var nextOff = off + 8 + size + (size & 1);
            // the spec pads an odd chunk to even; some writers do not — take the offset where a chunk id is
            if (size & 1) {
                var padded = KNOWN_CHUNKS.indexOf(tag4(u8, nextOff)) >= 0, bare = KNOWN_CHUNKS.indexOf(tag4(u8, nextOff - 1)) >= 0;
                if ((bare && !padded) || (!padded && !isTag(u8, nextOff) && isTag(u8, nextOff - 1))) nextOff--;
            }
            off = nextOff;
        }
        if (!fmt || !data || !(fmt.channels > 0) || !(fmt.sampleRate > 0)) return null;
        var bps = fmt.bits >> 3;
        var pcm = fmt.format === 1 && (fmt.bits === 8 || fmt.bits === 16 || fmt.bits === 24 || fmt.bits === 32);
        var flt = fmt.format === 3 && (fmt.bits === 32 || fmt.bits === 64);
        if (!(pcm || flt) || fmt.blockAlign !== bps * fmt.channels) return null;
        fmt.float = flt; fmt.offset = data.offset; fmt.bytes = data.bytes;
        return fmt;
    }

    async function readWav(file, L, sink, signal, onProgress) {
        // a recorder that never came back to write the size leaves 0 there: the payload runs to the end
        var end = L.bytes > 0 ? Math.min(file.size, L.offset + L.bytes) : file.size;
        var bps = L.bits >> 3, ba = L.blockAlign;
        var step = Math.max(ba, Math.floor(SLICE / ba) * ba);
        var out = new Float32Array(Math.floor(step / ba));
        sink.format(L.sampleRate, L.channels, Math.floor((end - L.offset) / ba));
        for (var pos = L.offset; pos + ba <= end; pos += step) {
            if (aborted(signal)) throw abortError();
            var stop = Math.min(end, pos + step);
            var buf = await file.slice(pos, stop).arrayBuffer();
            var dv = new DataView(buf), n = Math.floor(buf.byteLength / ba);
            for (var i = 0, o = 0; i < n; i++, o += ba) {
                var v;
                if (L.float) v = bps === 4 ? dv.getFloat32(o, true) : dv.getFloat64(o, true);
                else if (bps === 2) v = dv.getInt16(o, true) / 32768;
                else if (bps === 3) { var x = dv.getUint8(o) | (dv.getUint8(o + 1) << 8) | (dv.getInt8(o + 2) << 16); v = x / 8388608; }
                else if (bps === 4) v = dv.getInt32(o, true) / 2147483648;
                else v = (dv.getUint8(o) - 128) / 128;
                out[i] = v;
            }
            sink.pcm(out, n);
            if (onProgress) onProgress((stop - L.offset) / (end - L.offset));
        }
    }

    /* ── MP3: frames walked in slices, decoded by WebCodecs ── */
    function mp3T() { return root.Mp3FrameTrim; }

    function firstFrame(u8, from) {
        var T = mp3T();
        for (var i = from; i + 4 <= u8.length; i++) {
            if (u8[i] !== 0xFF || (u8[i + 1] & 0xE0) !== 0xE0) continue;
            var h = T.parseFrameHeader(u8, i);
            if (!h || h.layer !== 'Layer3') continue;
            var nx = T.parseFrameHeader(u8, i + h.frameLength);
            if (nx && nx.sampleRate === h.sampleRate && nx.layer === h.layer) return { offset: i, hdr: h };
        }
        return null;
    }

    /* The Xing/Info frame carries no sound. When LAME (or Lavc) wrote its tag after it, the
       encoder delay and padding are there too: FFmpeg — the export — drops delay + 529 decoder
       samples from the head and keeps frames × spf − delay − padding in all (measured: a 30-min
       file, 68 908 × 1152 − 576 − 1440 = 79 380 000 = 1800.000 s at 44.1 kHz). WebCodecs does
       neither, so the envelope ran ~40 ms late and 10 ms long against the file it cuts.
       Returns null (not a Xing frame) or { skip, keep } in samples (keep 0 = unknown). */
    function xingFrame(u8, off, len, spf) {
        var e = Math.min(off + len, off + 200, u8.length);
        for (var i = off + 4; i + 4 <= e; i++) {
            if ((u8[i] === 0x58 && u8[i + 1] === 0x69 && u8[i + 2] === 0x6E && u8[i + 3] === 0x67) ||
                (u8[i] === 0x49 && u8[i + 1] === 0x6E && u8[i + 2] === 0x66 && u8[i + 3] === 0x6F)) {
                var skip = 0, keep = 0, l = i + 120;
                var flags = i + 8 <= u8.length ? ((u8[i + 4] << 24) | (u8[i + 5] << 16) | (u8[i + 6] << 8) | u8[i + 7]) >>> 0 : 0;
                var frames = (flags & 1) && i + 12 <= u8.length ? ((u8[i + 8] << 24) | (u8[i + 9] << 16) | (u8[i + 10] << 8) | u8[i + 11]) >>> 0 : 0;
                if (l + 24 <= off + len && ((u8[l] === 0x4C && u8[l + 1] === 0x41 && u8[l + 2] === 0x4D && u8[l + 3] === 0x45) ||
                                           (u8[l] === 0x4C && u8[l + 1] === 0x61 && u8[l + 2] === 0x76))) {
                    var delay = (u8[i + 141] << 4) | (u8[i + 142] >> 4), pad = ((u8[i + 142] & 0x0F) << 8) | u8[i + 143];
                    skip = delay + 529;
                    if (frames > 0 && spf > 0) keep = Math.max(0, frames * spf - delay - pad);
                }
                return { skip: skip, keep: keep };
            }
        }
        return null;
    }

    /* Does this browser's decoder take several frames in one chunk and give back all of them?
       Chromium does (libavcodec keeps the rest of a packet), and it is 3.5× faster: one chunk per
       frame cost ~210 µs a frame — 14.7 s for 30 min of a 64-kbps book, ~5 min for 10 h — and 32
       frames a chunk 4.2 s (same samples to the last one). A decoder that takes only the first frame
       would drop the rest without a word, so it is asked on the file's own first frames. */
    async function groupedFramesDecode(probe, f0, sr, ch) {
        var T = mp3T(), AD = root.AudioDecoder, EAC = root.EncodedAudioChunk;
        var off = f0.offset, h = T.parseFrameHeader(probe, off);
        if (h && xingFrame(probe, off, h.frameLength, h.samples)) off += h.frameLength;
        var from = off, n = 0, want = 0;
        while (n < MP3_GROUP) {
            h = T.parseFrameHeader(probe, off);
            if (!h || h.sampleRate !== sr || off + h.frameLength > probe.length) break;
            want += h.samples; off += h.frameLength; n++;
        }
        if (n < 8) return false;
        var got = 0, bad = false, d = null;
        try {
            d = new AD({ output: function (a) { got += a.numberOfFrames; a.close(); }, error: function () { bad = true; } });
            d.configure({ codec: 'mp3', sampleRate: sr, numberOfChannels: ch });
            d.decode(new EAC({ type: 'key', timestamp: 0, data: probe.subarray(from, off) }));
            await d.flush();
        } catch (_) { bad = true; }
        try { if (d) d.close(); } catch (_) {}
        return !bad && got === want;
    }

    async function readMp3(file, sink, signal, onProgress, durationHint) {
        var T = mp3T(), AD = root.AudioDecoder, EAC = root.EncodedAudioChunk;
        if (!T || !AD || !EAC) throw expected('mp3 lane: no AudioDecoder');
        var head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
        var start = T.parseId3v2Length(head) || 0;
        var probe = new Uint8Array(await file.slice(start, start + 65536).arrayBuffer());
        var f0 = firstFrame(probe, 0);
        if (!f0) throw expected('mp3 lane: no MPEG audio stream');
        var sr = f0.hdr.sampleRate, ch = f0.hdr.channelMode === 3 ? 1 : 2;
        var support = null;
        try { support = await AD.isConfigSupported({ codec: 'mp3', sampleRate: sr, numberOfChannels: ch }); } catch (_) { support = null; }
        if (!support || !support.supported) throw expected('mp3 lane: codec unsupported');

        // window.__envMp3Group = 1 | 32 — stands only (A/B of the two feeds)
        var group = root.__envMp3Group > 0 ? root.__envMp3Group : ((await groupedFramesDecode(probe, f0, sr, ch)) ? MP3_GROUP : 1);
        var est = durationHint > 0 ? Math.ceil(durationHint * sr)
            : Math.ceil((file.size - start) * 8 / (f0.hdr.bitrate * 1000) * sr);
        sink.format(sr, ch, est);

        var failed = null, scratch = null, skip = 0, keep = 0, pushed = 0;
        var dec = new AD({
            output: function (data) {
                try {
                    var n = data.numberOfFrames;
                    if (!scratch || scratch.length < n) scratch = new Float32Array(Math.max(n, 4608));
                    data.copyTo(scratch, { planeIndex: 0, format: 'f32-planar' });
                    var from = 0;
                    if (skip > 0) { from = Math.min(skip, n); skip -= from; }
                    var m = n - from;
                    if (keep > 0) m = Math.min(m, Math.max(0, keep - pushed));
                    if (m > 0) { sink.pcm(from ? scratch.subarray(from, from + m) : scratch, m); pushed += m; }
                } catch (e) { failed = failed || e; } finally { data.close(); }
            },
            error: function (e) { failed = failed || e; }
        });
        dec.configure({ codec: 'mp3', sampleRate: sr, numberOfChannels: ch });

        var waitQueue = function () {
            return new Promise(function (resolve) {
                var done = false, t = null;
                var fin = function () {
                    if (done) return; done = true; if (t) clearTimeout(t);
                    try { dec.removeEventListener('dequeue', fin); } catch (_) {}
                    resolve();
                };
                try { dec.addEventListener('dequeue', fin); } catch (_) {}
                t = setTimeout(fin, 20);
            });
        };
        var sync = function (u8, o) {
            if (u8[o] !== 0xFF || (u8[o + 1] & 0xE0) !== 0xE0) return null;
            var h = T.parseFrameHeader(u8, o);
            return (h && h.sampleRate === sr && h.layer === 'Layer3') ? h : null;
        };

        var pos = start + f0.offset, carry = null, samples = 0, first = true, frames = 0, lost = 0;
        var end = file.size;
        /* Frames go to the decoder in runs of `group` CONTIGUOUS frames; a resync (skipped bytes)
           or the end of a slice closes the run. */
        var gFrom = -1, gTo = -1, gFrames = 0, gSamples = 0;
        var flushRun = function (buf) {
            if (!gFrames) return;
            dec.decode(new EAC({ type: 'key', timestamp: Math.round(samples / sr * 1e6),
                duration: Math.round(gSamples / sr * 1e6), data: buf.subarray(gFrom, gTo) }));
            samples += gSamples; frames += gFrames;
            gFrom = gTo = -1; gFrames = 0; gSamples = 0;
        };
        try {
            while (pos < end || carry) {
                if (aborted(signal)) throw abortError();
                if (failed) throw failed;
                var buf;
                if (pos < end) {
                    var next = Math.min(end, pos + SLICE);
                    var chunk = new Uint8Array(await file.slice(pos, next).arrayBuffer());
                    pos = next;
                    if (carry) { buf = new Uint8Array(carry.length + chunk.length); buf.set(carry); buf.set(chunk, carry.length); }
                    else buf = chunk;
                } else { buf = carry; }
                var atEnd = pos >= end;
                var i = 0;
                carry = null;
                while (i + 4 <= buf.length) {
                    var h = sync(buf, i);
                    if (!h) { i++; lost++; continue; }
                    var after = i + h.frameLength;
                    /* A frame is trusted when the next header follows it — a stray 0xFFE in a tag
                       or in damaged bytes decodes into noise (or kills the decoder) otherwise. The
                       next header may sit in the next slice: wait for it. The last frame of the
                       file has nothing after it and is taken whole or not at all. */
                    if (after + 4 > buf.length) {
                        if (!atEnd) break;
                        if (after > buf.length) { i = buf.length; break; }
                    } else if (!sync(buf, after)) {
                        // the stream may end here (a tag follows): take the frame only at the very end
                        if (!(atEnd && !firstFrame(buf.subarray(after), 0))) { i++; lost++; continue; }
                    }
                    if (first) {
                        first = false;
                        var xf = xingFrame(buf, i, h.frameLength, h.samples);
                        if (xf) { skip = xf.skip; keep = xf.keep; i = after; continue; }
                    }
                    if (gFrames && gTo !== i) flushRun(buf);
                    if (!gFrames) gFrom = i;
                    gTo = after; gFrames++; gSamples += h.samples;
                    i = after;
                    if (gFrames >= group) {
                        flushRun(buf);
                        while (dec.decodeQueueSize > MP3_QUEUE_MAX) { await waitQueue(); if (failed) throw failed; }
                    }
                }
                flushRun(buf);
                carry = (!atEnd && i < buf.length) ? buf.slice(i) : null;
                if (onProgress) onProgress(Math.min(1, (pos - start) / Math.max(1, end - start)));
                await new Promise(function (r) { setTimeout(r, 0); });
            }
            await dec.flush();
            if (failed) throw failed;
        } finally {
            try { dec.close(); } catch (_) {}
        }
        if (!frames) throw expected('mp3 lane: no frames');
        return { lost: lost, frames: frames, group: group };
    }

    /* ── mp4 family: decodeRange in its streaming form ── */
    async function readMp4(file, sink, signal, onProgress) {
        var WAD = root.WebCodecsAudioDecode;
        if (!WAD || typeof WAD.decodeRange !== 'function' || !root.AudioDecoder) throw expected('mp4 lane: no decoder');
        var est = 0, chans = 2, started = false;
        await WAD.decodeRange(file, null, {
            from: 0, sinkChannels: 1, signal: signal, onProgress: onProgress,
            onInfo: function (inf) {
                var d = inf.movieSec > 0 ? inf.movieSec : inf.trackSec;
                chans = inf.channels || 2;
                est = d > 0 ? d : 0;
            },
            sink: function (planes, rate) {
                if (!started) { started = true; sink.format(rate, chans, est > 0 ? Math.ceil(est * rate) : 0); }
                sink.pcm(planes[0], planes[0].length);
            }
        });
        if (!started) throw expected('mp4 lane: no audio decoded');
    }

    /* ── anything else FFmpeg reads: windows decoded from the MOUNTED file (30.10.2026) ──
       RU telemetry 03–30.10, /chapter-splitter: after the three lanes above, books still hit the
       device-memory wall when no lane could read them — MP3 in a browser without WebCodecs (Firefox
       for Android 571 MB, Safari on iPhone 137 MB, «~250 min against a ceiling of ~181 min»), ALAC in
       an .m4a («no decoder for sound entry alac», 236 MB on a phone), and FLAC / Ogg / Opus / ADTS-AAC
       went to the whole decode by design. FFmpeg (already on the page for the export) reads all of
       them from the mounted file: each window is `-ss T -t W` → channel 0 as raw float, and is cut
       (or padded) to EXACTLY W seconds of samples, so the windows tile the book without drift. It is
       the same input seek the export cuts chapters with, so a point placed on this envelope is where
       the exported chapter starts. Slower than the native lanes (a decode per window), hence last. */
    var FF_WINDOW_SEC = 120;
    function logDuration(log) {
        var m = /Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/.exec(log || '');
        return m ? (+m[1]) * 3600 + (+m[2]) * 60 + parseFloat(m[3]) : 0;
    }
    async function readFfmpeg(file, sink, signal, onProgress, durationHint) {
        var FP = root.FFmpegProcessor;
        if (!FP || typeof FP.process !== 'function' || typeof FP.probeAudioStream !== 'function') throw expected('ffmpeg lane: no engine');
        var ok = false;
        try { ok = await FP.init(); } catch (_) { ok = false; }
        if (!ok) throw expected('ffmpeg lane: the engine did not start');
        if (aborted(signal)) throw abortError();
        var info = await FP.probeAudioStream(file);
        var sr = info && info.sampleRate > 0 ? info.sampleRate : 0, ch = (info && info.channels) || 2;
        if (!sr) throw expected('ffmpeg lane: no audio stream');
        /* WHERE THE BOOK ENDS — two traps, both measured on real files (30.10.2026):
           - a raw ADTS stream has no length in its header: FFmpeg ESTIMATES it from the bitrate (and
             says so in its log), and the estimate ran 40 s past the end of a 67-min book;
           - an Ogg (Vorbis, Opus) asked to seek PAST its end hands back its last page (1.2 s of the
             tail) instead of nothing — a reader waiting for an empty window never stops (the first cut
             of this lane looped to window 3 778, «-ss 453360»).
           So: a window clearly shorter than its length is the last one (the end is inside it); and no
           window starts at or past FFmpeg's OWN duration unless FFmpeg says it only estimated it. A
           window a frame or two short (≤ 0.5 s) is filled with silence: the next one still starts
           where its time is. */
        var ffLog = '';
        if (typeof FP.probeStreaming === 'function') { try { ffLog = await FP.probeStreaming(file); } catch (_) { ffLog = ''; } }
        var ffDur = logDuration(ffLog);
        var estimated = /Estimating duration from bitrate/i.test(ffLog);
        var fence = ffDur > 0 && !estimated ? ffDur : Infinity;
        var dur = durationHint > 0 ? durationHint : ffDur;
        sink.format(sr, ch, dur > 0 ? Math.ceil(dur * sr) : 0);
        var win = Math.round(FF_WINDOW_SEC * sr), tol = Math.round(0.5 * sr), got = 0, zeros = null;
        for (var k = 0; ; k++) {
            if (aborted(signal)) throw abortError();
            var t0 = k * FF_WINDOW_SEC;
            if (k > 0 && t0 >= fence - 0.05) break;
            var res = await FP.process(file, ['-map', '0:a:0', '-af', 'pan=mono|c0=c0', '-c:a', 'pcm_f32le', '-f', 'f32le'],
                'envelope-' + k + '.raw', null, ['-ss', t0.toFixed(3), '-t', String(FF_WINDOW_SEC)]);
            if (res && res.url) { try { URL.revokeObjectURL(res.url); } catch (_) {} }
            if (aborted(signal)) throw abortError();
            var bytes = res && res.blob ? await res.blob.arrayBuffer() : null;
            var pcm = bytes ? new Float32Array(bytes, 0, bytes.byteLength >> 2) : new Float32Array(0);
            if (!pcm.length) break;
            var n = Math.min(pcm.length, win);
            sink.pcm(pcm, n);
            got += n;
            if (onProgress && dur > 0) onProgress(Math.min(1, got / (dur * sr)));
            if (n < win - tol) break;
            if (n < win) {
                if (!zeros) zeros = new Float32Array(win);
                sink.pcm(zeros, win - n);
                got += win - n;
            }
        }
        if (!got) throw expected('ffmpeg lane: nothing decoded');
        return { ffmpegWindows: Math.ceil(got / win) };
    }

    async function lane(file) {
        if (!file || !(file.size > 44)) return null;
        var u8 = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
        if (wavLayout(u8)) return 'wav';
        if (u8.length >= 12 && String.fromCharCode(u8[4], u8[5], u8[6], u8[7]) === 'ftyp') {
            return (root.WebCodecsAudioDecode && root.AudioDecoder) ? 'mp4' : null;
        }
        if (!root.Mp3FrameTrim || !root.AudioDecoder) return null;
        var start = root.Mp3FrameTrim.parseId3v2Length(u8) || 0;
        var probe = start ? new Uint8Array(await file.slice(start, start + 65536).arrayBuffer()) : u8;
        return firstFrame(probe, 0) ? 'mp3' : null;
    }

    async function read(file, opts) {
        opts = opts || {};
        var signal = opts.signal || null;
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        var which = opts.lane === 'ffmpeg' ? 'ffmpeg' : await lane(file);
        var canFf = opts.fallback === 'ffmpeg' && !!root.FFmpegProcessor;
        if (!which && canFf) which = 'ffmpeg';
        if (!which) throw expected('envelope: no streaming lane for this file');
        var acc = null, info = null;
        var sink = {
            format: function (sr, ch, estFrames) {
                info = { sampleRate: sr, channels: ch };
                acc = accumulator(sr, { estFrames: estFrames || (opts.durationHint > 0 ? Math.ceil(opts.durationHint * sr) : 0),
                    columns: opts.columns, size: opts.size });
            },
            pcm: function (ch0, n) { acc.push(ch0, n); }
        };
        var t0 = Date.now(), extra = null, from = '';
        try {
            if (which === 'wav') {
                var u8 = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
                await readWav(file, wavLayout(u8), sink, signal, onProgress);
            } else if (which === 'mp3') {
                extra = await readMp3(file, sink, signal, onProgress, opts.durationHint);
            } else if (which === 'mp4') {
                await readMp4(file, sink, signal, onProgress);
            } else {
                extra = await readFfmpeg(file, sink, signal, onProgress, opts.durationHint);
            }
        } catch (e) {
            // a native lane that cannot read THIS file hands it to FFmpeg — from the start: format()
            // builds a fresh accumulator. Any failure but a cancel: the mp4 lane's refusal of ALAC
            // («no decoder for sound entry alac») is a plain Error, not an `.expected` one
            if (which === 'ffmpeg' || !canFf || aborted(signal) || (e && e.name === 'AbortError')) throw e;
            from = which;
            which = 'ffmpeg';
            acc = null; info = null;
            extra = await readFfmpeg(file, sink, signal, onProgress, opts.durationHint);
        }
        if (!acc) throw expected('envelope: nothing decoded');
        var r = acc.finish();
        if (!(r.frames > 0)) throw expected('envelope: empty');
        r.lane = which; r.channels = info.channels; r.ms_elapsed = Date.now() - t0;
        if (from) r.fallbackFrom = from;
        if (extra && extra.lost) r.lostBytes = extra.lost;
        if (extra && extra.group) r.group = extra.group;
        if (extra && extra.ffmpegWindows) r.windows = extra.ffmpegWindows;
        return r;
    }

    root.AudioEnvelopeStream = { lane: lane, read: read, accumulator: accumulator, _wavLayout: wavLayout, _logDuration: logDuration, FF_WINDOW_SEC: FF_WINDOW_SEC };
})(typeof window !== 'undefined' ? window : globalThis);
